import React, {useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import Avatar from "../../system/components/avatar";
import {logout} from "../../auth/auth-slice";
import {usePermission} from "../../system/usePermission";
import {fetchAppData, setMessage} from "../../system/app-slice";
import {getSiteConfigs, setSiteConfigs} from "../../system/storage";
import LanguageItem from "../../system/components/language-item";
import ProfileSetting from "../settings/tabs/profile-setting";
import Archive from "../settings/tabs/archive";
import Subscriptions from "../settings/tabs/subscriptions";
import AccessControl from "../settings/tabs/access-control";
import ManageMembers from "../settings/tabs/manage-members";
import {MobilePageHeader} from "./mobile-common";

const settingsSections = [
    {key: "archive", label: "archive", icon: "pi pi-inbox", component: Archive},
    {key: "subscriptions", label: "subscription", icon: "pi pi-credit-card", component: Subscriptions},
    {key: "access-control", label: "access-control", icon: "pi pi-shield", permission: "modify-access-control", component: AccessControl},
    {key: "manage-members", label: "manage-members", icon: "pi pi-users", component: ManageMembers},
];

export default function MobileSettings({canManageWorkspace = false, detailId, navigate, translate, onWorkspaceClick}) {
    const dispatch = useDispatch();
    const permission = usePermission();
    const membership = useSelector((state) => state.app.workspaceMembershipData);
    const config = useSelector((state) => state.app.config || {});
    const user = useSelector((state) => state.auth.user);
    const paymentPending = membership?.workspace?.workspaceStatus === "payment_pending";
    const visibleSections = useMemo(() => settingsSections.filter((section) => {
        if (!canManageWorkspace) return false;
        if (paymentPending) return section.key === "subscriptions";
        return !section.permission || permission.can(section.permission);
    }), [canManageWorkspace, paymentPending, permission]);
    const selected = visibleSections.find((section) => section.key === detailId);
    const currentLanguage = config["support-languages"]?.find((language) => (
        language.code === (getSiteConfigs()?.language?.code || config.language)
    ));

    const updateLanguage = (language) => {
        setSiteConfigs({...getSiteConfigs(), language});
        dispatch(fetchAppData());
    };
    const updateAppearance = (value) => {
        setSiteConfigs({...getSiteConfigs(), "dark-mode": value === "dark"});
        dispatch(fetchAppData());
    };
    const showUnavailable = () => dispatch(setMessage({
        severity: "info",
        content: translate("mobile.settings.backend-unavailable"),
    }));

    if (detailId === "user-profile") {
        return (
            <div className="flex flex-column gap-2 h-full min-h-0">
                <MobilePageHeader title={translate("profile")} onBack={() => navigate("/mobile/settings")}/>
                <div className="flex-1 min-h-0 overflow-auto surface-card mobile-brand-radius"><ProfileSetting/></div>
            </div>
        );
    }

    if (detailId === "language-region") {
        return (
            <SettingDetailShell title={translate("mobile.settings.language-region")} navigate={navigate}>
                <Dropdown
                    className="w-full min-h-3rem text-sm shabnam-font mobile-brand-radius"
                    panelClassName={`${config.dir} shabnam-font`}
                    options={config["support-languages"] || []}
                    value={currentLanguage}
                    valueTemplate={(option, props) => <LanguageItem option={option} props={props}/>} 
                    itemTemplate={(option, props) => <LanguageItem option={option} props={props}/>} 
                    onChange={(event) => updateLanguage(event.value)}
                />
            </SettingDetailShell>
        );
    }

    if (detailId === "appearance") {
        const currentAppearance = config["dark-mode"] ? "dark" : "light";
        const options = [{value: "light", icon: "pi pi-sun"}, {value: "dark", icon: "pi pi-moon"}];
        return (
            <SettingDetailShell title={translate("mobile.settings.appearance")} navigate={navigate}>
                <div className="grid m-0">
                    {options.map((option) => (
                        <div className="col-6 p-1" key={option.value}>
                            <Button
                                type="button"
                                outlined={currentAppearance !== option.value}
                                className="w-full min-h-5rem flex-column justify-content-center gap-2 mobile-brand-radius"
                                icon={option.icon}
                                label={translate(option.value)}
                                onClick={() => updateAppearance(option.value)}
                            />
                        </div>
                    ))}
                </div>
            </SettingDetailShell>
        );
    }

    if (selected) {
        const Section = selected.component;
        return (
            <div className="flex flex-column gap-2 h-full min-h-0">
                <MobilePageHeader title={translate(selected.label)} onBack={() => navigate("/mobile/settings")}/>
                <div className="flex-1 min-h-0 overflow-auto surface-card mobile-brand-radius"><Section/></div>
            </div>
        );
    }

    return (
        <div className="flex flex-column gap-3">
            <MobilePageHeader title={translate("mobile.more.settings")} description={translate("mobile.more.settings-description")} onBack={() => navigate("/mobile/more")}/>

            <button type="button" className="mobile-settings-profile-card w-full min-h-6rem flex align-items-center gap-3 text-right mobile-brand-bg-soft border-1 mobile-brand-border-soft mobile-brand-radius p-3 mobile-brand-shadow cursor-pointer" onClick={() => navigate("/mobile/settings/user-profile")}> 
                <span className="w-3rem h-3rem flex align-items-center justify-content-center border-circle mobile-brand-bg text-white flex-shrink-0"><Avatar memberData={membership?.member || user} size="large"/></span>
                <span className="flex-1 min-w-0">
                    <strong className="block text-sm text-900 mb-1 text-overflow-ellipsis overflow-hidden white-space-nowrap">{user?.fullname || membership?.member?.fullname}</strong>
                    <small className="text-500 text-overflow-ellipsis overflow-hidden white-space-nowrap">{membership?.workspace?.name}</small>
                </span>
                <span className="text-xs text-700 flex-shrink-0">{translate("edit")}</span>
            </button>

            {!paymentPending && <SettingsGroup title={translate("mobile.settings.account-workspace")}>
                <SettingsRow icon="pi pi-users" label={translate("mobile.settings.workspace-selection")} description={membership?.workspace?.name} trailingText={translate("mobile.settings.change")} onClick={onWorkspaceClick}/>
                <SettingsRow icon="pi pi-language" label={translate("mobile.settings.language-region")} description={currentLanguage ? translate(currentLanguage.name) : translate("persian")} onClick={() => navigate("/mobile/settings/language-region")}/>
            </SettingsGroup>}

            {!paymentPending && <SettingsGroup title={translate("mobile.settings.notifications-focus")}>
                <SettingsRow icon="pi pi-bell" label={translate("notification-settings")} description={translate("mobile.settings.notification-settings-description")} onClick={() => navigate("/mobile/notification-settings")}/>
            </SettingsGroup>}

            {!paymentPending && <SettingsGroup title={translate("mobile.settings.application-support")}>
                <SettingsRow icon="pi pi-palette" label={translate("mobile.settings.appearance")} description={translate(config["dark-mode"] ? "dark" : "light")} onClick={() => navigate("/mobile/settings/appearance")}/>
                <SettingsRow icon="pi pi-download" label={translate("mobile.settings.offline-access")} description={translate("mobile.settings.backend-required")} control={<InputSwitch checked disabled aria-label={translate("mobile.settings.offline-access")}/>} onClick={showUnavailable}/>
                <SettingsRow icon="pi pi-lock" label={translate("mobile.settings.security-devices")} description={translate("mobile.settings.backend-required")} onClick={showUnavailable}/>
                <SettingsRow icon="pi pi-question-circle" label={translate("mobile.settings.help-support")} description={translate("mobile.settings.help-support-description")} onClick={() => navigate("/mobile/help")}/>
            </SettingsGroup>}

            {paymentPending && visibleSections.length > 0 && <SettingsGroup title={translate("mobile.settings.workspace-management")}>
                {visibleSections.map((section) => <SettingsRow key={section.key} icon={section.icon} label={translate(section.label)} onClick={() => navigate(`/mobile/settings/${section.key}`)}/>) }
            </SettingsGroup>}

            <Button type="button" outlined severity="danger" className="w-full min-h-3rem text-xs gap-2 mt-1 mobile-brand-radius justify-content-center" label={translate("logout")} onClick={() => dispatch(logout())}/>
        </div>
    );
}

function SettingsGroup({title, children}) {
    return (
        <section className="flex flex-column gap-2">
            <h2 className="text-2xs text-500 font-normal m-0 px-2">{title}</h2>
            <div className="mobile-settings-card surface-card border-1 border-100 mobile-brand-radius px-3 mobile-brand-shadow overflow-hidden">{children}</div>
        </section>
    );
}

function SettingsRow({icon, label, description, onClick, control, trailingText}) {
    const content = (
        <>
            <span className="w-2rem h-2rem flex align-items-center justify-content-center surface-100 text-600 mobile-brand-radius flex-shrink-0"><i className={`${icon} text-sm`}/></span>
            <span className="flex-1 min-w-0 text-right">
                <strong className="block text-xs text-900">{label}</strong>
                {description && <small className="block text-2xs text-500 mt-1 text-overflow-ellipsis overflow-hidden white-space-nowrap">{description}</small>}
            </span>
            {trailingText && <span className="text-2xs text-500 flex-shrink-0">{trailingText}</span>}
            {control || (onClick && <i className="pi pi-angle-left text-500 text-xs"/>)}
        </>
    );
    const className = "w-full min-h-3rem flex align-items-center gap-2 border-none border-bottom-1 border-100 surface-card py-2 px-0";
    return onClick
        ? <button type="button" className={`${className} cursor-pointer`} onClick={onClick}>{content}</button>
        : <div className={className}>{content}</div>;
}

function SettingDetailShell({title, navigate, children}) {
    return (
        <div className="flex flex-column gap-3 h-full min-h-0">
            <MobilePageHeader title={title} onBack={() => navigate("/mobile/settings")}/>
            <section className="surface-card border-1 border-100 mobile-brand-radius p-3 mobile-brand-shadow">{children}</section>
        </div>
    );
}

export function MobileProfile({navigate, translate}) {
    return (
        <div className="flex flex-column gap-2 h-full min-h-0">
            <MobilePageHeader title={translate("profile")} onBack={() => navigate(-1)}/>
            <div className="flex-1 min-h-0 overflow-auto surface-card mobile-brand-radius"><ProfileSetting onCancel={() => navigate(-1)}/></div>
        </div>
    );
}
